
"use client";
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, onSnapshot, type Firestore } from 'firebase/firestore';
import { db } from '@/lib/firebase'; 
import { useRemoteConfig } from './RemoteConfigContext'; // Import useRemoteConfig

interface SiteStatusDataFromFirestore { // Renamed to distinguish from context value
  showBanner: boolean;
  bannerMessage: string;
  bannerType: 'info' | 'warning' | 'error';
  // maintenanceModeActive is now sourced from Remote Config
}

interface SiteStatusContextType {
  showBanner: boolean;
  bannerMessage: string;
  bannerType: 'info' | 'warning' | 'error';
  maintenanceModeActive: boolean; // This will come from RemoteConfig
  loadingStatus: boolean; // Combined loading state
}

const SiteStatusContext = createContext<SiteStatusContextType | undefined>(undefined);

export const SiteStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { maintenanceModeActive: maintenanceModeFromRemoteConfig, loadingConfig: loadingRemoteConfigFlag } = useRemoteConfig();
  
  const [bannerStatus, setBannerStatus] = useState<SiteStatusDataFromFirestore>({
    showBanner: false,
    bannerMessage: '',
    bannerType: 'warning',
  });
  const [loadingFirestoreStatus, setLoadingFirestoreStatus] = useState(true);

  useEffect(() => {
    if (!db || (db as unknown as Firestore)?.type !== 'firestore') {
      console.warn("SiteStatusProvider: Firestore is not available or not correctly initialized for banner status.");
      setLoadingFirestoreStatus(false);
      return;
    }

    const statusDocRef = doc(db, 'siteStatus', 'global');
    const unsubscribe = onSnapshot(statusDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setBannerStatus({
          showBanner: data?.showBanner ?? false,
          bannerMessage: data?.bannerMessage || '',
          bannerType: data?.bannerType || 'warning',
        });
      } else {
        setBannerStatus({
          showBanner: false,
          bannerMessage: '',
          bannerType: 'info',
        });
      }
      setLoadingFirestoreStatus(false);
    }, (error) => {
      console.error("SiteStatusProvider: Error fetching site banner status from Firestore:", error);
      setBannerStatus({
          showBanner: false,
          bannerMessage: '',
          bannerType: 'info',
        });
      setLoadingFirestoreStatus(false);
    });

    return () => unsubscribe();
  }, []);

  const combinedLoadingStatus = loadingFirestoreStatus || loadingRemoteConfigFlag;

  return (
    <SiteStatusContext.Provider value={{ 
      ...bannerStatus, 
      maintenanceModeActive: maintenanceModeFromRemoteConfig, 
      loadingStatus: combinedLoadingStatus 
    }}>
      {children}
    </SiteStatusContext.Provider>
  );
};

export const useSiteStatus = (): SiteStatusContextType => {
  const context = useContext(SiteStatusContext);
  if (context === undefined) {
    throw new Error('useSiteStatus must be used within a SiteStatusProvider');
  }
  return context;
};
